// ============================================================================
// Quick Actions Component
// Feature: 035-app-shell-enhancement
// Created: 2026-01-21
// Description: Component for displaying quick action commands in the palette
//              when no search query is entered. Provides common operations
//              and shortcuts for power users.
// User Story: US6 (Keyboard Shortcuts)
// ============================================================================

import React from 'react';
import { Command } from 'cmdk';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: string;
  shortcut?: string;
  action: () => void;
  roles?: string[];
}

interface QuickActionsProps {
  /** Callback when an action is selected */
  onAction: (action: QuickAction) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Quick actions component for the command palette
 * 
 * Features:
 * - Common operations available without search
 * - Role-based action filtering
 * - Keyboard shortcuts display
 * - Navigation and utility actions
 * - Power user shortcuts
 */
export const QuickActions: React.FC<QuickActionsProps> = ({
  onAction,
  className,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Define available quick actions
  const getQuickActions = (): QuickAction[] => {
    return [
      // Navigation actions
      {
        id: 'dashboard',
        title: 'Go to Dashboard',
        description: 'Navigate to the main dashboard',
        icon: '📊',
        shortcut: 'D',
        action: () => navigate('/dashboard'),
        roles: ['admin', 'operator', 'viewer'],
      },
      {
        id: 'workspaces',
        title: 'Browse Workspaces',
        description: 'View all workspace resources',
        icon: '🏢',
        shortcut: 'W',
        action: () => navigate('/workspaces'),
        roles: ['admin', 'operator', 'viewer'],
      },
      {
        id: 'backup-runs',
        title: 'View Backup Runs',
        description: 'Monitor backup operations',
        icon: '💾',
        shortcut: 'B',
        action: () => navigate('/backup'),
        roles: ['admin', 'operator', 'viewer'],
      },
      {
        id: 'alerts',
        title: 'Check Alerts',
        description: 'View system alerts and notifications',
        icon: '🔔',
        shortcut: 'A',
        action: () => navigate('/alerts'),
        roles: ['admin', 'operator'],
      },

      // Admin-only actions
      {
        id: 'settings',
        title: 'System Settings',
        description: 'Configure system preferences',
        icon: '⚙️',
        shortcut: 'S',
        action: () => navigate('/settings'),
        roles: ['admin'],
      },
      {
        id: 'users',
        title: 'Manage Users',
        description: 'User administration and roles',
        icon: '👥',
        shortcut: 'U',
        action: () => navigate('/users'),
        roles: ['admin'],
      },

      // Operations actions
      {
        id: 'new-policy',
        title: 'Create New Policy',
        description: 'Create a new backup policy',
        icon: '🛡️',
        action: () => navigate('/backup/plans'),
        roles: ['admin', 'operator'],
      },
      {
        id: 'restore',
        title: 'Start Restore',
        description: 'Initiate a restore operation',
        icon: '🔄',
        action: () => navigate('/backup/restore'),
        roles: ['admin', 'operator'],
      },
      {
        id: 'maintenance',
        title: 'Maintenance Mode',
        description: 'Access maintenance dashboard',
        icon: '🔧',
        action: () => navigate('/maintenance'),
        roles: ['admin', 'operator'],
      },

      // Analytics actions
      {
        id: 'costs',
        title: 'View Cost Analysis',
        description: 'Analyze storage and compute costs',
        icon: '💰',
        shortcut: 'C',
        action: () => navigate('/backup'),
        roles: ['admin', 'operator', 'viewer'],
      },
      {
        id: 'reports',
        title: 'Generate Reports',
        description: 'Create compliance and audit reports',
        icon: '📄',
        action: () => navigate('/backup'),
        roles: ['admin', 'operator', 'viewer'],
      },

      // Utility actions
      {
        id: 'refresh',
        title: 'Refresh Data',
        description: 'Reload current page data',
        icon: '🔄',
        shortcut: 'R',
        action: () => window.location.reload(),
        roles: ['admin', 'operator', 'viewer'],
      },
      {
        id: 'help',
        title: 'Show Help',
        description: 'Open help documentation',
        icon: '❓',
        shortcut: '?',
        action: () => window.open('/docs', '_blank'),
        roles: ['admin', 'operator', 'viewer'],
      },
    ];
  };

  // Filter actions based on user role
  const filteredActions = getQuickActions().filter(action => {
    if (!action.roles) return true;
    return action.roles.includes(user?.role || 'viewer');
  });

  // Group actions by category
  const groupedActions = {
    navigation: filteredActions.filter(action => 
      ['dashboard', 'workspaces', 'backup-runs', 'alerts'].includes(action.id)
    ),
    operations: filteredActions.filter(action => 
      ['new-policy', 'restore', 'maintenance'].includes(action.id)
    ),
    analytics: filteredActions.filter(action => 
      ['costs', 'reports'].includes(action.id)
    ),
    admin: filteredActions.filter(action => 
      ['settings', 'users'].includes(action.id)
    ),
    utility: filteredActions.filter(action => 
      ['refresh', 'help'].includes(action.id)
    ),
  };

  // Handle action selection
  const handleSelect = (action: QuickAction) => {
    onAction(action);
    action.action();
  };

  // Render action group
  const renderActionGroup = (title: string, actions: QuickAction[]) => {
    if (!actions.length) return null;

    return (
      <div key={title} className="py-2">
        <div className="px-4 py-1 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
          {title}
        </div>
        {actions.map((action) => (
          <Command.Item
            key={action.id}
            value={action.id}
            onSelect={() => handleSelect(action)}
            className={clsx(
              'flex items-center justify-between gap-3 px-4 py-2 cursor-pointer',
              'hover:bg-[var(--color-bg-subtle)] transition-colors duration-150',
              'data-[selected=true]:bg-blue-50 dark:data-[selected=true]:bg-blue-900/20 data-[selected=true]:border-l-2 data-[selected=true]:border-blue-500'
            )}
          >
            <div className="flex items-center gap-3">
              {/* Icon */}
              <div className="flex-shrink-0 text-lg">
                {action.icon}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--color-text-primary)]">
                  {action.title}
                </div>
                <div className="text-xs text-[var(--color-text-secondary)]">
                  {action.description}
                </div>
              </div>
            </div>

            {/* Keyboard shortcut */}
            {action.shortcut && (
              <div className="flex-shrink-0">
                <kbd className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] rounded">
                  <span className="text-xs">⌘</span>
                  {action.shortcut}
                </kbd>
              </div>
            )}
          </Command.Item>
        ))}
      </div>
    );
  };

  return (
    <div className={clsx('divide-y divide-[var(--color-border)]', className)}>
      {renderActionGroup('Navigation', groupedActions.navigation)}
      {renderActionGroup('Operations', groupedActions.operations)}
      {renderActionGroup('Analytics', groupedActions.analytics)}
      {renderActionGroup('Administration', groupedActions.admin)}
      {renderActionGroup('Utilities', groupedActions.utility)}

      {/* Help text */}
      <div className="px-4 py-3 text-xs text-[var(--color-text-secondary)] border-t border-[var(--color-border)]">
        <p className="mb-1">💡 <strong>Tip:</strong> Start typing to search for specific resources</p>
        <p>Use keyboard shortcuts when available for faster access</p>
      </div>
    </div>
  );
};

export default QuickActions;